/**
 * Cómo se escribe una marca de tiempo en las tablas.
 *
 * Vive suelto y no dentro de cada tabla porque son tres —ejecuciones, llamadas
 * y auditoría— y una columna «cuándo» que significa cosas distintas según la
 * pantalla es peor que cualquiera de las opciones por separado.
 *
 * Módulo sin JSX para poder probarlo con el ejecutor de tests de Node.
 */

/**
 * Las marcas llegan de ClickHouse como `2026-08-31 18:04:57.000`, sin zona.
 * Son UTC, así que hay que decirlo: sin la `Z`, el navegador las interpreta
 * como hora local y el panel muestra desfases de horas sin avisar de nada.
 */
function aFecha(marca: string): Date {
  return new Date(`${marca.replace(' ', 'T')}Z`);
}

/**
 * Fecha y hora, siempre las dos.
 *
 * Antes estas columnas mostraban solo la hora. Con una ventana de un día se
 * entiende; con una de treinta, «18:04:57» no dice de qué día habla y dos
 * filas seguidas pueden estar a semanas de distancia. Mostrar la fecha siempre
 * cuesta unos caracteres y evita esa lectura equivocada, que además no tiene
 * cómo delatarse.
 *
 * El año solo aparece cuando no es el corriente. El rollup guarda 400 días, o
 * sea que una ventana larga puede cruzar de año: sin el año esas filas serían
 * ambiguas, y ponerlo en todas alargaría cada fila para el 99% de los casos en
 * que no aporta.
 */
export function fechaYHora(marca: string, ahora: Date = new Date()): string {
  const fecha = aFecha(marca);
  if (Number.isNaN(fecha.getTime())) return marca;

  return fecha.toLocaleString('es-CL', {
    day: '2-digit',
    month: '2-digit',
    ...(fecha.getFullYear() === ahora.getFullYear() ? {} : { year: 'numeric' }),
    hour: '2-digit',
    minute: '2-digit',
    second: '2-digit',
    hour12: false,
  });
}
